import { useEffect, useState } from "react";
import { DataRequestStateEnum, DataRequestType } from "@/core/types/dataRequestsTypes";
import useDataRequestState from "@/core/dataProviders/hooks/useDataRequestState";
import useDataRequestOrder from "@/core/dataProviders/hooks/useDataRequestOrder";
import { useAppSelector } from "@/core/store/reduxHooks";
import { PostType } from "@/core/types/contentTypes";
import { PostSlugMapSelector, PostsSelector } from "@/core/store/bucket/BucketSlice";
import useLangSelector from "@/core/dataProviders/hooks/useLangSelector";
import getContent from "@/core/dataProviders/utils/getContent";
import isNewOrderNeeded from "@/core/dataProviders/utils/isNewOrderNeeded";
import { useRouter } from "next/router";

/**
 * data provider for front-end pages based on available options for given data
 * - checks if order exists / creates orders / returns order status
 * - gathers and returns requested data
 * */

const usePostByIdProvider = (
  sign: { id: string; isSimple?: boolean; slug?: string; postPreviewId?: string } | null
): [PostType | null, DataRequestStateEnum] => {
  const orderData = useDataRequestOrder();
  const posts = useLangSelector(useAppSelector(PostsSelector));
  const slugToPostId = useLangSelector(useAppSelector(PostSlugMapSelector));

  const router = useRouter();

  const id = sign ? sign.id : null;
  const isSimple = sign ? sign.isSimple : null;
  const slug = sign ? sign.slug : null;

  const [currentPost, setCurrentPost] = useState<PostType | null>(() =>
    sign ? getContent(sign.id, posts, slugToPostId, !!sign.isSimple) : null
  );
  const [currentReqState, , setReqSign] = useDataRequestState({
    type: DataRequestType.POST_BY_ID,
    sign: sign,
  });

  useEffect(() => {
    sign ? setCurrentPost(posts?.[parseInt(sign?.id)]!) : setCurrentPost(null);
    setReqSign(sign);
  }, [id, isSimple]);

  useEffect(() => {
    sign ? setCurrentPost(posts?.[parseInt(sign?.id)]!) : setCurrentPost(null);
  }, [slugToPostId, posts]);

  useEffect(() => {
    if (!sign || isNewOrderNeeded(!!currentPost, currentReqState)) {
      return;
    }

    orderData({
      type: DataRequestType.POST_BY_ID,
      sign: sign,
    });

    if (slug && currentPost?.slug !== slug && currentPost?.link) {
      // TODO: fix missing 301 status code
      router.push(currentPost?.link);
    }
  }, [currentReqState, orderData]);

  return [currentPost, currentReqState];
};

export default usePostByIdProvider;
